import { ArrowCounterClockwiseIcon, GaugeIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { useMediaTransmitters } from "@/api/media-transmitters/queries";
import type { MediaTransmitter } from "@/api/media-transmitters/types";
import { ResetFramesAlertDialog } from "@/components/media-transmitters/reset-frames-alert-dialog";
import {
	type Column,
	MeterTable,
	usePagination,
} from "@/components/meters/meter-table";
import { CopyableCell } from "@/components/shared/copyable-cell";
import { Button } from "@/components/ui/button";
import { EmptyCell } from "@/components/ui/empty-cell";
import { isValidLang, type Lang, ROUTE_SLUGS } from "@/config/routes";
import { useDebounce } from "@/hooks/use-debounce";
import { useResetSourceFlow } from "@/hooks/use-reset-source-flow";
import { formatDateTime } from "@/utils/format-datetime";

export function MediaTransmittersTable() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { lang } = useParams<{ lang: string }>();
	const currentLang: Lang = lang && isValidLang(lang) ? lang : "pl";

	const { page, setPage } = usePagination();
	const [pageSize, setPageSize] = useState(20);
	const [ordering, setOrdering] = useState("");
	const [search, setSearch] = useState("");
	const debouncedSearch = useDebounce(search);
	const resetFlow = useResetSourceFlow();

	const { data, isLoading, isError, refetch } = useMediaTransmitters({
		page,
		page_size: pageSize,
		search: debouncedSearch || undefined,
		ordering: ordering || undefined,
	});

	const columns: Column<MediaTransmitter>[] = [
		{
			key: "sn",
			label: t("mediaTransmitters.colSerialNumber"),
			sortField: "sn",
			render: (row) => (
				<CopyableCell
					value={row.sn}
					label={t("mediaTransmitters.colSerialNumber")}
				/>
			),
		},
		{
			key: "total_frames",
			label: t("mediaTransmitters.colFrames"),
			sortField: "total_frames",
			render: (row) => row.total_frames,
		},
		{
			key: "created_at",
			label: t("mediaTransmitters.colCreatedAt"),
			sortField: "created_at",
			render: (row) => formatDateTime(row.created_at, currentLang),
		},
		{
			key: "last_communication",
			label: t("mediaTransmitters.colLastCommunication"),
			sortField: "last_communication",
			render: (row) =>
				row.last_communication ? (
					formatDateTime(row.last_communication, currentLang)
				) : (
					<EmptyCell />
				),
		},
		{
			key: "_actions",
			label: "",
			render: (row) => (
				<div className="flex items-center justify-end gap-2">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => resetFlow.open(row.sn)}
					>
						<ArrowCounterClockwiseIcon
							aria-hidden="true"
							className="mr-2 h-4 w-4"
						/>
						{t("mediaTransmitters.actionResetAll")}
					</Button>
					<Button
						variant="ghost"
						size="sm"
						onClick={() =>
							navigate(
								`/${currentLang}/${ROUTE_SLUGS.mediaTransmitters[currentLang]}/${row.sn}/${ROUTE_SLUGS.detectedMeters[currentLang]}`,
							)
						}
					>
						<GaugeIcon aria-hidden="true" className="mr-2 h-4 w-4" />
						{t("mediaTransmitters.actionDetectedMeters")}
					</Button>
				</div>
			),
		},
	];

	return (
		<>
			<MeterTable
				data={data}
				columns={columns}
				loading={isLoading}
				isError={isError}
				onRetry={() => refetch()}
				page={page}
				pageSize={pageSize}
				onPageChange={(p) => setPage(p)}
				onPageSizeChange={(s) => {
					setPageSize(s);
					setPage(1);
				}}
				search={search}
				onSearchChange={(s) => {
					setSearch(s);
					setPage(1);
				}}
				searchPlaceholder={t("mediaTransmitters.searchPlaceholder")}
				ordering={ordering}
				onOrderingChange={(o) => {
					setOrdering(o);
					setPage(1);
				}}
				rowKeyField="sn"
			/>
			<ResetFramesAlertDialog
				open={resetFlow.pendingSn !== null}
				onOpenChange={(o) => !o && resetFlow.close()}
				pending={resetFlow.isPending}
				scope="source"
				target={resetFlow.pendingSn ?? ""}
				onConfirm={resetFlow.confirm}
			/>
		</>
	);
}
